// The Players screen's server state (spec/13 §8, F-ORG-05/-06, F-IAM-07, F-CMP-02), as TanStack
// Query hooks over the generated client — `packages/contracts/src/routes/{players,player-bans,id-checks}.ts`:
//
//   GET   …/organizations/{o}/players?q=&tag=                  the organisation's own players   players:read
//   GET   …/players/{p}                                        one player's profile             players:read
//   PATCH …/players/{p}                                        notes and tags                   players:notes
//   GET   …/players/{p}/bans                                   the player's bans                players:ban
//   POST  …/venues/{v}/players/{p}/ban  · …/players/{p}/ban    ban at a venue / the organisation players:ban
//   POST  …/venues/{v}/players/{p}/unban · …/players/{p}/unban lift it                          players:ban
//   GET   …/players/{p}/id-checks                              the staff document checks        players:id_check
//   POST  …/players/{p}/id-checks                              record one, Idempotency-Key      players:id_check
//
// Players are organisation-scoped (spec/03 §11): every read and write names the organisation in its
// path, so the venue console and the organisation console share these hooks. A ban is taken **at** a
// venue when there is one (the venue-scoped route), and across the organisation from the organisation
// console (the organisation-scoped route); the server decides whether the actor may do either.

import type {
  ApiClient,
  BanPlayer,
  IdCheck,
  LiftBan,
  OrganizationPlayer,
  OrganizationPlayerProfile,
  PlayerBan,
  RecordIdCheck,
  UpdateOrganizationPlayer,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { useScopeIds } from '../../shell/scope';

/** A page of players. The api's list is small; a screenful at a time is plenty for a search. */
const PAGE_SIZE = 25;

/** The api searches from two characters (or a whole phone, email or member QR); below that there is nothing to ask. */
export const PLAYER_SEARCH_MIN = 2;

/** How many matches a picker lists under its field (Sell, a refund, Start session): the first of the first page. */
const PICKER_RESULTS = 8;

/**
 * Players moved from the café's previous system (F-HQ-40, spec/13 §8.2): `GET …/organizations/{o}/imported-players`, a
 * page at a time, narrowed to the venue at a venue, searched by old username, name, a whole phone or a whole email.
 * Read only: claiming a profile is a later feature. `players:read`.
 */
export function useImportedPlayers(organizationId: string, venueId: string | undefined, q: string) {
  const api = useApi();
  const query = q.trim();
  return useInfiniteQuery({
    queryKey: ['players', organizationId, 'imported', venueId ?? '', query] as const,
    queryFn: ({ pageParam }) =>
      api.listImportedPlayers({
        path: { organizationId },
        query: { limit: PAGE_SIZE, ...(venueId === undefined ? {} : { venueId }), ...(query === '' ? {} : { q: query }), ...(pageParam === undefined ? {} : { cursor: pageParam }) },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** What a counter claim sends (F-IAM-13): to an existing player, or to an email the player proves with a code. */
export type CounterClaimBody = Parameters<ApiClient['claimAtCounter']>[0]['body'];

/**
 * Claiming a moved profile at the counter (F-IAM-13, spec/13 §8.2): a code to the email the player gives, the managers
 * who can approve with their PIN, and the claim. `wallet:topup` (and `players:id_check`), checked again by the server.
 */
export function useCounterClaim(organizationId: string, importedPlayerId: string | null) {
  const api = useApi();
  const queryClient = useQueryClient();
  const sendCode = useMutation({
    mutationFn: (email: string) => api.requestCounterClaimEmailCode({ path: { organizationId, importedPlayerId: importedPlayerId ?? '' }, body: { email } }),
  });
  const claim = useMutation({
    mutationFn: (body: CounterClaimBody) => api.claimAtCounter({ path: { organizationId, importedPlayerId: importedPlayerId ?? '' }, body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['players', organizationId] }),
  });
  return { sendCode, claim };
}

/** The managers who can approve this claim with their staff PIN here (asked for once the money needs it). */
export function useClaimApprovers(organizationId: string, importedPlayerId: string | null, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['players', organizationId, 'imported', 'approvers', importedPlayerId ?? ''] as const,
    queryFn: () => api.listCounterClaimApprovers({ path: { organizationId, importedPlayerId: importedPlayerId ?? '' } }),
    enabled: enabled && importedPlayerId !== null,
  });
}

/** Moved balances whose claim window ends soon or has ended, for the owner's write-off (`credit:write_off`). */
export function useCarriedOverWriteOffs(organizationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['players', organizationId, 'imported', 'write-offs'] as const,
    queryFn: () => api.listCarriedOverWriteOffs({ path: { organizationId } }),
    enabled,
  });
}

export function useWriteOffCarriedOver(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { importedPlayerIds: string[]; reason?: string }) => api.writeOffCarriedOver({ path: { organizationId }, body: { ...body, confirm: true } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['players', organizationId] }),
  });
}

export const playerKeys = {
  list: (organizationId: string, q: string, tag: string) => ['players', organizationId, 'list', q, tag] as const,
  profile: (organizationId: string, playerId: string) => ['players', organizationId, 'profile', playerId] as const,
  bans: (organizationId: string, playerId: string) => ['players', organizationId, 'bans', playerId] as const,
  idChecks: (organizationId: string, playerId: string) => ['players', organizationId, 'id-checks', playerId] as const,
  sessions: (organizationId: string, playerId: string) => ['players', organizationId, 'sessions', playerId] as const,
  orders: (organizationId: string, playerId: string) => ['players', organizationId, 'orders', playerId] as const,
  passes: (organizationId: string, playerId: string) => ['players', organizationId, 'passes', playerId] as const,
  points: (organizationId: string, playerId: string) => ['players', organizationId, 'points', playerId] as const,
  games: (organizationId: string, playerId: string) => ['players', organizationId, 'games', playerId] as const,
};

/**
 * What the player played at the venues the member works at (F-CAT-04, spec/13 §8): per-title totals and the newest
 * plays, from `GET …/players/{playerId}/games`. One read, not paged — the totals are the point.
 */
export function usePlayerGames(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: playerKeys.games(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: () => api.listPlayerGames({ path: { organizationId, playerId: playerId ?? '' } }),
  });
}

/** A page of history rows: a screenful at a time, newest first, across every venue the member works at. */
const HISTORY_PAGE = 25;

/**
 * The one player search (spec/13 §8 rule 2): the Players list, and every picker that finds a player — Sell's,
 * a refund to the wallet, Start session. The organisation's players, a page at a time. Empty `q` lists them all
 * (newest dealings first); a `q` searches among them — a part of a name or username, or a phone, email or member
 * QR whole. A `tag` narrows to one list. A picker passes `enabled` (two characters typed, no player chosen yet)
 * and reads `pickerResults`; one cache entry per search, shared by every screen that asks it.
 */
export function usePlayerSearch(organizationId: string, q: string, options: { readonly tag?: string; readonly enabled?: boolean } = {}) {
  const api = useApi();
  const query = q.trim();
  const tag = options.tag ?? '';
  return useInfiniteQuery({
    queryKey: playerKeys.list(organizationId, query, tag),
    enabled: options.enabled ?? true,
    queryFn: ({ pageParam }) =>
      api.listPlayers({
        path: { organizationId },
        query: {
          limit: PAGE_SIZE,
          ...(query === '' ? {} : { q: query }),
          ...(tag === '' ? {} : { tag }),
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** What a picker lists under its field: the first matches of a search, or none until enough is typed. */
export function pickerResults(search: ReturnType<typeof usePlayerSearch>, q: string): readonly OrganizationPlayer[] {
  if (q.trim().length < PLAYER_SEARCH_MIN) return [];
  return (search.data?.pages[0]?.data ?? []).slice(0, PICKER_RESULTS);
}

/** One player as this organisation sees them: profile basics, tags and notes (F-ORG-05/-06). */
export function usePlayerProfile(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery<OrganizationPlayerProfile>({
    queryKey: playerKeys.profile(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: () => api.getPlayer({ path: { organizationId, playerId: playerId ?? '' } }),
  });
}

/**
 * Change a player's notes and tags (F-ORG-06, `players:notes`). Each change is audited; on success the
 * profile and the list are re-read so the new tags and notes appear.
 */
export function useUpdatePlayer(organizationId: string, playerId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrganizationPlayer) => api.updatePlayer({ path: { organizationId, playerId }, body }),
    onSuccess: (profile) => {
      queryClient.setQueryData(playerKeys.profile(organizationId, playerId), profile);
      void queryClient.invalidateQueries({ queryKey: ['players', organizationId, 'list'] });
    },
  });
}

/** The player's bans across the organisation — in force, lapsed and lifted (F-IAM-07). */
export function usePlayerBans(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery<readonly PlayerBan[]>({
    queryKey: playerKeys.bans(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: async () => (await api.listPlayerBans({ path: { organizationId, playerId: playerId ?? '' } })).data,
  });
}

/**
 * Ban a player at the current venue, or across the organisation when there is no venue (F-IAM-07).
 * The server enforces who may do either; this only sends the request the scope decides.
 */
export function useBanPlayer(organizationId: string, playerId: string, venueId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BanPlayer) => banPlayer(api, organizationId, playerId, venueId, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: playerKeys.bans(organizationId, playerId) }),
  });
}

/** Lift a ban at the current venue, or across the organisation (F-IAM-07). A lift needs a reason too. */
export function useUnbanPlayer(organizationId: string, playerId: string, venueId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LiftBan) => unbanPlayer(api, organizationId, playerId, venueId, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: playerKeys.bans(organizationId, playerId) }),
  });
}

/** The staff document checks this organisation holds for the player, newest first (F-CMP-02). */
export function useIdChecks(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery<readonly IdCheck[]>({
    queryKey: playerKeys.idChecks(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: async () => (await api.listIdChecks({ path: { organizationId, playerId: playerId ?? '' } })).data,
  });
}

/**
 * Record a staff document check (F-CMP-02). The caller keeps the `Idempotency-Key` for as long as it
 * sends the same body, so a retry replays the check instead of making a second one (spec/08 §2.2).
 */
export function useRecordIdCheck(playerId: string) {
  const api = useApi();
  const scope = useScopeIds();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { readonly body: RecordIdCheck; readonly idempotencyKey: string }) =>
      api.recordIdCheck({ path: { organizationId: scope.organizationId, playerId }, headerParams: { 'idempotency-key': idempotencyKey }, body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: playerKeys.idChecks(scope.organizationId, playerId) }),
  });
}

/** The player's sessions across the organisation, newest first (F-ORG-05, spec/13 §8). Filled by F-SES. */
export function usePlayerSessions(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: playerKeys.sessions(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: ({ pageParam }) =>
      api.listPlayerSessions({ path: { organizationId, playerId: playerId ?? '' }, query: { limit: HISTORY_PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** The player's food orders across the organisation, newest first (spec/13 §8). Filled by F-POS. */
export function usePlayerOrders(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: playerKeys.orders(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: ({ pageParam }) =>
      api.listPlayerOrders({ path: { organizationId, playerId: playerId ?? '' }, query: { limit: HISTORY_PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** The passes the player holds across the organisation (spec/13 §8). Filled by F-PRC. */
export function usePlayerPasses(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: playerKeys.passes(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: ({ pageParam }) =>
      api.listPlayerPasses({ path: { organizationId, playerId: playerId ?? '' }, query: { limit: HISTORY_PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** The player's points ledger across the organisation, newest first (spec/13 §8). Filled by F-LOY. */
export function usePlayerPoints(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: playerKeys.points(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: ({ pageParam }) =>
      api.listPlayerPoints({ path: { organizationId, playerId: playerId ?? '' }, query: { limit: HISTORY_PAGE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

function banPlayer(api: ApiClient, organizationId: string, playerId: string, venueId: string | undefined, body: BanPlayer): Promise<PlayerBan> {
  return venueId === undefined
    ? api.banPlayer({ path: { organizationId, playerId }, body })
    : api.banPlayerAtVenue({ path: { organizationId, venueId, playerId }, body });
}

function unbanPlayer(api: ApiClient, organizationId: string, playerId: string, venueId: string | undefined, body: LiftBan): Promise<PlayerBan> {
  return venueId === undefined
    ? api.unbanPlayer({ path: { organizationId, playerId }, body })
    : api.unbanPlayerAtVenue({ path: { organizationId, venueId, playerId }, body });
}

/** One line for a player: display name, then gamer tag, then given names, then the id. */
export function playerName(player: Pick<OrganizationPlayer, 'displayName' | 'username' | 'firstName' | 'lastName' | 'id'>): string {
  const names = [player.firstName, player.lastName].filter((part): part is string => part !== null && part !== '').join(' ');
  return player.displayName ?? player.username ?? (names === '' ? player.id : names);
}
